import { useMediaQuery, useTheme } from '@material-ui/core';
import { uploadImageAsync } from '@orientaction/utils';
import classnames from 'classnames';
import clsx from 'clsx';
import { FC, forwardRef, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import CircularProgress from '@material-ui/core/CircularProgress';
import styles from './style';

const baseStyle2 = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  width: '100%',
  height: '50px',
  cursor: 'pointer',
};

const focusedStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const getBaseStyle = (
  match: boolean,
  useForGame: boolean,
  fullSize: boolean,
  useForModel: boolean,
  isSmaller: boolean
) => {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: fullSize ? 0 : '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: useForModel ? '#f1f1f1' : '#fafafa',
    outline: 'none',
    transition: 'border .24s ease-in-out',
    width: fullSize ? '100%' : useForGame ? 260 : match ? 125 : isSmaller ? 95 : 150,
    height: fullSize ? 514 : useForGame ? 364 : match ? 125 : isSmaller ? 95 : 150,
    cursor: 'pointer',
  };
};

const getImageStyle = (match: boolean) => {
  return {
    display: 'block',
    width: '100%',
    height: '100%',
  };
};

interface IProps {
  fileUrl?: string;
  pathPrefix?: string;
  placeholder?: any;
  handleFileUrlChange: (url: any) => void;
  overrideClassname?: any;
  sectionTop?: boolean;
  error?: boolean;
  overrideThumbsContainer?: string;
  overrideThumb?: string;
  overrideThumbInner?: string;
  handleFileChange?: (file: any) => void;
  position?: any;
  keyComponent?: any;
}

const placeholderImage = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
    <span style={{ fontSize: '32px', fontWeight: 'normal', color: '#5763FF' }}>+</span>
    <span style={{ fontSize: '14px', color: '#5763FF' }}>Ajouter</span>
  </div>
);

const TestMainImage: FC<IProps & any> = forwardRef(
  (
    {
      fileUrl = '',
      handleFileUrlChange,
      pathPrefix = 'tests',
      overrideClassname = '',
      sectionTop = false,
      error = false,
      overrideThumbsContainer = '',
      overrideThumb = '',
      overrideThumbInner = '',
      useForGame = false,
      handleFileChange,
      fullSize = false,
      isFullHeight = false,
      useForModel = false,
      position,
      keyComponent,
      isSmaller = false,
      placeholder = placeholderImage,
    }: any,
    ref
  ) => {
    const [isUploading, setIsUploading] = useState(false);
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down('md'));
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
      accept: 'image/*',
      onDrop: async acceptedFiles => {
        const uri = URL.createObjectURL(acceptedFiles[0]);
        if (useForGame) {
          handleFileUrlChange(uri);
          handleFileChange(acceptedFiles[0]);
        } else {
          setIsUploading(true);
          try {
            const res = await uploadImageAsync(uri, pathPrefix);
            handleFileUrlChange(res);
          } catch (err: any) {
            console.log('error on upload', err);
          } finally {
            setIsUploading(false);
          }
        }
      },
    });

    const cls = styles();

    const style: any = useMemo(
      () => ({
        ...(sectionTop
          ? getBaseStyle(matches, useForGame, fullSize, useForModel, isSmaller)
          : baseStyle2),
        ...(isFocused ? focusedStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
        ...(isFullHeight ? { height: '100%' } : {}),
      }),
      [
        sectionTop,
        matches,
        isFocused,
        isDragAccept,
        isDragReject,
        useForGame,
        fullSize,
        isFullHeight,
        useForModel,
        isSmaller,
      ]
    );

    const imageStyle = getImageStyle(matches);
    const generateKey = () => Math.random().toString(36).substr(2, 9);

    return (
      <div
        ref={ref as any}
        className={classnames(cls.dropzoneContainer, overrideClassname)}
        style={{
          ...(isFullHeight ? { height: '100%' } : {}),
          ...(useForModel ? { width: '100%' } : {}),
        }}>
        {fileUrl.length > 0 ? (
          <div
            style={{ ...(useForModel ? { width: '100%', height: '100%' } : {}) }}
            className={classnames(cls.thumbsContainer, overrideThumbsContainer)}
            key={`editor2-${Math.random()}`}>
            <div
              className={classnames(cls.thumb, overrideThumb)}
              style={{
                ...(fullSize
                  ? { width: '100%', height: isFullHeight ? '100%' : 514 }
                  : useForGame
                  ? { width: 304, height: 408 }
                  : {}),
                ...(isSmaller ? { width: 95 } : {}),
              }}>
              <div
                style={{
                  ...(useForModel
                    ? {
                        width: '100%',
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                      }
                    : {}),
                  ...(useForGame ? { width: '100%', height: '100%' } : {}),
                }}
                className={classnames(cls.thumbInner, overrideThumbInner)}>
                <img
                  style={{
                    ...(useForModel || useForGame ? { objectFit: 'contain' } : {}),
                    ...imageStyle,
                    ...(useForModel
                      ? {
                          position: 'absolute',
                          width: `${(position && position.scale ? position.scale : 1) * 100}%`,
                          height: `${(position && position.scale ? position.scale : 1) * 100}%`,
                          top: `${position && position.position ? position.position : 0}%`,
                        }
                      : {}),
                  }}
                  src={fileUrl}
                  alt="thumbs"
                  key={keyComponent || generateKey()}
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              ...(fullSize ? { width: '100%' } : {}),
              ...(isFullHeight ? { height: '100%' } : {}),
            }}
            className={clsx({
              [cls.inputError]: error,
            })}>
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              {isUploading ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                  <CircularProgress style={{ color: '#5763FF' }} />
                  <span style={{ fontSize: '14px', color: '#5763FF' }}>Chargement...</span>
                </div>
              ) : (
                placeholder()
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);
export default TestMainImage;
